// live-map.jsx — React wrapper around the map renderer.
//  • Classic vector product  -> MapLibre GL (crisp at every zoom, buildings)
//  • Aerial / Land-use        -> the original main-thread canvas renderer (TileMap)
// Both share the same caption/border/print pipeline below.
const { useRef: useR, useEffect: useE } = React;
const GL_OK = typeof maplibregl !== "undefined";
const useGLFor = (theme) => GL_OK && window.SouvenirGL && window.SouvenirGL.isGLTheme(theme);

function LiveMapPoster(props) {
  return useGLFor(props.theme)
    ? React.createElement(GLPoster, props)
    : React.createElement(CanvasPoster, props);
}

// ---- shaped borders (circle / heart): the map is clipped to the shape --------
// ONE set of coordinates drives the on-screen clip, the on-screen outline and the
// print path, so the preview and the printed sheet cannot drift apart.
const HEART_100 = "M50,100 C50,100 0,72 0,36 C0,14 16,0 32,0 C42,0 47,6 50,11 C53,6 58,0 68,0 C84,0 100,14 100,36 C100,72 50,100 50,100 Z";
const HEART_UNIT = "M0.5,1 C0.5,1 0,0.72 0,0.36 C0,0.14 0.16,0 0.32,0 C0.42,0 0.47,0.06 0.5,0.11 C0.53,0.06 0.58,0 0.68,0 C0.84,0 1,0.14 1,0.36 C1,0.72 0.5,1 0.5,1 Z";
const SHAPE_FILL = 0.92;   // shape side as a fraction of the map box short edge
const isShaped = (b) => b === "circle" || b === "heart";
// Lavender and Harbor Blue shaped posters (circle / heart) print on a white sheet:
// the shape's interior keeps the palette's own map colours, the page around it goes
// white so the silhouette reads as a cut-out. Every other palette keeps its tinted sheet.
const WHITE_SHEET = { "Lavender": 1, "Harbor Blue": 1, "Sakura": 1 };
const sheetBg = (theme, border) => (isShaped(border) && WHITE_SHEET[theme && theme.name] ? "#ffffff" : theme.bg);
// Trace the shape onto a canvas context, in the same proportions as the CSS clip.
function shapePath(ctx, shape, x, y, w, h) {
  ctx.beginPath();
  if (shape === "circle") { ctx.arc(x + w / 2, y + h / 2, Math.min(w, h) / 2, 0, Math.PI * 2); ctx.closePath(); return; }
  const P = (px, py) => [x + px * w / 100, y + py * h / 100];
  const start = P(50, 100);
  ctx.moveTo(start[0], start[1]);
  const c = (a, b, d) => { const A = P(a[0], a[1]), B = P(b[0], b[1]), C = P(d[0], d[1]); ctx.bezierCurveTo(A[0], A[1], B[0], B[1], C[0], C[1]); };
  c([50, 100], [0, 72], [0, 36]);
  c([0, 14], [16, 0], [32, 0]);
  c([42, 0], [47, 6], [50, 11]);
  c([53, 6], [58, 0], [68, 0]);
  c([84, 0], [100, 14], [100, 36]);
  c([100, 72], [50, 100], [50, 100]);
  ctx.closePath();
}

// ---- shared poster shell (grain, legend, 3 caption layouts) -----------------
function renderShell(props, mapEl) {
  const { theme, titleFont, grain, paper, labels } = props;
  const e = React.createElement;
  const layout = labels.show ? (labels.layout || "caption") : "none";
  const capColor = theme.labelColor || theme.text;
  // Circle/heart borders clip the MAP to the shape and leave the sheet flat, so the
  // roads and water end at the outline. The wrapper is rendered for EVERY border so the
  // map holder never changes position in the tree — switching border must not remount
  // (and so destroy) the live MapLibre instance.
  const shape = isShaped(props.frameMat) ? props.frameMat : null;
  const isoTall = shape && props.paper && props.paper.aspect > 1.02;
  // Portrait AND square shaped posters size the map box to the shape EXACTLY (no
  // headroom, no flex growth), so the shape cannot float inside its own box. All
  // leftover height then belongs solely to the wrapper's justifyContent:center,
  // which splits it evenly above the shape and below the caption — giving
  // page-top→shape == text→page-bottom at every aspect ratio.
  const shapeFixed = shape && props.paper && props.paper.aspect >= 0.995;
  // Shape side as a % of poster WIDTH. Normally SHAPE_FILL, but on a near-square
  // page a 92%-of-width square plus the caption is taller than the page, so cap it
  // by the height actually left over once the caption's share is reserved.
  const CAP_FRAC = 0.24;
  const aspect = (props.paper && props.paper.aspect) || 1;
  const sideW = Math.min(SHAPE_FILL * 100, aspect * (1 - CAP_FRAC) * 100);
  // The caption's last row is styled line-height:0, so its glyphs overshoot the row
  // box; reserve just that overshoot (~1% of the poster HEIGHT, expressed against
  // width since CSS padding % always resolves against width).
  const capPadBot = shapeFixed ? (1.0 * aspect).toFixed(2) + "%" : "6.5%";
  // Shaped portrait pages get a roomier shape→caption gap: at 1.2% the rule sat
  // almost against the heart/circle. The wrapper re-centres the taller group, so
  // the equal top/bottom page margins are preserved.
  const capPadTop = isoTall ? "3.6% " : shape ? "1.5% " : "5.5% ";
  const sq = shapeFixed ? (sideW + "cqw") : "min(" + SHAPE_FILL * 100 + "cqw," + SHAPE_FILL * 100 + "cqh)";
  mapEl = e("div", { key: "mapbox", ref: props.boxRef || null, style: Object.assign({ minHeight: 0, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", containerType: "size" },
    shapeFixed ? { flex: "0 0 auto", height: "calc(" + sideW + "cqw)" } : { flex: "1 1 auto" }) },
    e("div", { key: "clip", style: shape
      ? { position: "relative", flex: "0 0 auto", width: sq, aspectRatio: "1 / 1", display: "flex", overflow: "hidden", borderRadius: shape === "circle" ? "50%" : 0, clipPath: shape === "heart" ? "url(#svnr-heart-clip)" : "none" }
      : { position: "relative", width: "100%", height: "100%", display: "flex", overflow: "hidden" } }, [
      mapEl,
      shape ? e("svg", { key: "edge", viewBox: "0 0 100 100", preserveAspectRatio: "none", style: { position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible", zIndex: 2 } }, [
        e("defs", { key: "defs" }, e("clipPath", { id: "svnr-heart-clip", clipPathUnits: "objectBoundingBox" }, e("path", { d: HEART_UNIT }))),
        shape === "heart"
          ? e("path", { key: "p", d: HEART_100, fill: "none", stroke: capColor, strokeOpacity: 0.85, strokeWidth: 1, vectorEffect: "non-scaling-stroke" })
          : e("circle", { key: "c", cx: 50, cy: 50, r: 49.6, fill: "none", stroke: capColor, strokeOpacity: 0.85, strokeWidth: 1, vectorEffect: "non-scaling-stroke" }),
      ]) : null,
    ]));

  const grainEl = grain ? e("div", { key: "gr", style: { position: "absolute", inset: 0, pointerEvents: "none", mixBlendMode: "multiply", opacity: 0.05, backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" } }) : null;
  const legendCols = theme.palette && window.landuseLegend ? window.landuseLegend(theme.palette) : (theme.legend || []);
  const legendRow = (h, gap) => legendCols.length ? e("div", { key: "lg", style: { display: "flex", gap: gap, width: "100%", height: h } },
    legendCols.map((c, i) => e("span", { key: i, style: { flex: 1, background: c } }))) : null;
  const wrapStyle = { width: "100%", height: "100%", display: "flex", flexDirection: "column", background: sheetBg(theme, props.frameMat), position: "relative", overflow: "hidden", containerType: "size" };

  if (layout === "1a") {
    return e("div", { className: "live-poster", style: wrapStyle }, [
      mapEl, grainEl,
      e("div", { key: "band", style: { flex: "0 0 auto", padding: "4.5% 6% 5.5%", color: capColor, display: "flex", flexDirection: "column", gap: "clamp(10px,2.4cqh,30px)" } }, [
        legendRow("clamp(6px,2.2cqh,24px)", "clamp(2px,0.5cqh,5px)"),
        e("div", { key: "row", style: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "1em" } }, [
          e("div", { key: "city", style: { fontFamily: titleFont, fontWeight: 800, fontSize: "clamp(20px," + (paper.landscape ? 6 : 8.5) + "cqh,110px)", lineHeight: 0.9, letterSpacing: "-0.03em", textTransform: "lowercase", whiteSpace: "nowrap" } }, labels.title || "your city"),
          e("div", { key: "meta", style: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "clamp(6px,1.4cqh,16px)", flex: "0 0 auto" } }, [
            labels.showCoords && labels.coords ? e("div", { key: "c", style: { fontWeight: 700, fontSize: "clamp(8px,1.7cqh,20px)", whiteSpace: "nowrap" } }, labels.coords) : null,
            labels.subtitle ? e("div", { key: "s", style: { background: theme.text, color: theme.bg, padding: "0.3em 1em", fontWeight: 800, fontSize: "clamp(8px,1.7cqh,20px)", letterSpacing: "0.04em", textTransform: "lowercase" } }, labels.subtitle) : null,
          ]),
        ]),
      ]),
    ]);
  }
  if (layout === "1b") {
    return e("div", { className: "live-poster", style: Object.assign({}, wrapStyle, { padding: "clamp(18px,5cqh,64px) clamp(18px,5cqw,64px)" }) }, [
      e("div", { key: "top", style: { flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", color: capColor, marginBottom: "clamp(10px,2.6cqh,34px)" } }, [
        labels.subtitle ? e("div", { key: "s", style: { fontWeight: 600, fontSize: "clamp(8px,1.7cqh,20px)", letterSpacing: "0.42em", textTransform: "uppercase" } }, labels.subtitle) : e("span", { key: "s" }),
        labels.showCoords && labels.coords ? e("div", { key: "c", style: { fontWeight: 500, fontSize: "clamp(8px,1.7cqh,20px)", letterSpacing: "0.08em", whiteSpace: "nowrap" } }, labels.coords) : null,
      ]),
      e("div", { key: "mapwrap", style: { flex: "1 1 auto", minHeight: 0, position: "relative", display: "flex", flexDirection: "column" } }, mapEl),
      grainEl,
      e("div", { key: "rule", style: { flex: "0 0 auto", height: 1, background: theme.text, opacity: 0.35, marginTop: "clamp(10px,2.6cqh,34px)" } }),
      e("div", { key: "city", style: { flex: "0 0 auto", fontFamily: titleFont, fontWeight: 800, fontSize: "clamp(16px," + (paper.landscape ? 4 : 5.6) + "cqh,68px)", lineHeight: 0.9, letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap", marginTop: "clamp(8px,1.8cqh,24px)", color: theme.text } }, labels.title || "YOUR CITY"),
      e("div", { key: "lgwrap", style: { flex: "0 0 auto", marginTop: "clamp(8px,1.8cqh,22px)" } }, legendRow("clamp(6px,2.2cqh,24px)", "clamp(2px,0.5cqh,5px)")),
    ]);
  }
  return e("div", { className: "live-poster", style: shapeFixed ? Object.assign({}, wrapStyle, { justifyContent: "center" }) : wrapStyle }, [
    mapEl, grainEl,
    labels.show ? e("div", { key: "cap", style: { flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: capPadTop + "6% " + capPadBot, gap: "0.5em", color: capColor, position: "relative", zIndex: 2 } }, [
      labels.subtitleTop ? e("div", { key: "rule", style: { display: "flex", alignItems: "center", gap: "0.9em", width: "62%", color: theme.satellite ? (theme.labelColor || theme.text) : (theme.marker || theme.major), opacity: theme.satellite ? 0.62 : 1 } }, [
        e("span", { key: "l", style: { flex: 1, height: "max(1px,0.1cqh)", background: "currentColor", opacity: 0.6 } }),
        e("span", { key: "d", style: { width: "max(4px,0.9cqh)", height: "max(4px,0.9cqh)", flex: "0 0 auto", borderRadius: "50%", background: "currentColor" } }),
        e("span", { key: "r", style: { flex: 1, height: "max(1px,0.1cqh)", background: "currentColor", opacity: 0.6 } }),
      ]) : null,
      e("div", { key: "t", "data-cap": "t", style: { fontFamily: titleFont, fontWeight: 600, letterSpacing: labels.spaced ? "0.12em" : "0.02em", textTransform: labels.upper ? "uppercase" : "none", fontSize: "clamp(13px, " + (paper.landscape ? 5 : 5.4) + "cqh, 72px)", lineHeight: 1.04, marginTop: isoTall ? "0.55em" : 0 } }, labels.title || "Your Place"),
      labels.subtitle ? e("div", { key: "s", "data-cap": "s", style: { fontSize: "clamp(7px,1.9cqh,20px)", letterSpacing: "0.3em", textTransform: "uppercase", opacity: theme.satellite ? 1 : 0.92, color: theme.satellite ? capColor : (theme.marker || capColor) } }, labels.subtitle) : null,
      labels.showCoords && labels.coords ? e("div", { key: "c", "data-cap": "c", style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: "clamp(6px,1.5cqh,13px)", letterSpacing: "0.18em", opacity: 0.62, marginTop: "0.55em" } }, labels.coords) : null,
    ]) : null,
  ]);
}

// ---- MapLibre GL poster (classic vector product) ----------------------------
function GLPoster(props) {
  const { location, theme, markerStyle, paper, labels, apiRef } = props;
  const ZMIN = (typeof window !== "undefined" && window.SOUVENIR_MINZOOM) || 10;
  const ZMAX = (typeof window !== "undefined" && window.SOUVENIR_MAXZOOM) || 14;
  const clamp = (v) => Math.max(ZMIN, Math.min(ZMAX, v));
  const holderRef = useR(null);
  const boxRef = useR(null);
  const mapRef = useR(null);
  const markerRef = useR(null);
  const overlayRef = useR(null);
  const loupeRef = useR(null);
  const roRef = useR(null);
  const lastZoom = useR(location.zoom || 14);
  const p = useR(props); p.current = props;

  const setMarker = () => {
    const map = mapRef.current; if (!map) return;
    if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
    const pr = p.current;
    if (!pr.markerStyle || pr.markerStyle === "none") return;
    const mkTheme = pr.markerColor ? Object.assign({}, pr.theme, { marker: pr.markerColor }) : pr.theme;
    const { el, anchor } = window.SouvenirGL.makeMarkerEl(pr.markerStyle, mkTheme, pr.markerScale || 1);
    el.style.zIndex = "3"; // sit above the WYSIWYG overlay image (z-index 1)
    // Draggable to fine-tune, and auto-recentres whenever the customer pans (see
    // the map "move" handler below).
    const mk = new maplibregl.Marker({ element: el, anchor: anchor, draggable: true })
      .setLngLat(map.getCenter()).addTo(map);
    mk.on("dragstart", () => { mk._dragging = true; });
    mk.on("dragend", () => {
      mk._dragging = false;
      const ll = mk.getLngLat();
      const cb = p.current.onPin; if (cb) cb(ll.lng, ll.lat);
    });
    markerRef.current = mk;
  };

  // build a fresh lightweight "tm" for the capture pipeline
  const glTm = () => {
    const map = mapRef.current;
    const c = map.getCenter(); const mk = markerRef.current;
    const pin = mk ? mk.getLngLat() : c;
    return {
      isGL: true, canvas: map.getCanvas(),
      boxW: (boxRef.current && boxRef.current.clientWidth) || 0, boxH: (boxRef.current && boxRef.current.clientHeight) || 0,
      view: { lng: c.lng, lat: c.lat, zoom: map.getZoom() },
      pin: { lng: pin.lng, lat: pin.lat },
      markerStyle: p.current.markerStyle, dpr: window.devicePixelRatio || 1,
    };
  };

  useE(() => {
    const map = new maplibregl.Map({
      container: holderRef.current, style: window.buildGLStyle(theme, { labels: props.showLabels }),
      center: [location.lng, location.lat], zoom: location.zoom || 14, minZoom: ZMIN, maxZoom: ZMAX,
      attributionControl: false, dragRotate: false, pitchWithRotate: false, fadeDuration: 0, preserveDrawingBuffer: true,
    });
    map.touchZoomRotate.disableRotation();
    mapRef.current = map;

    // WYSIWYG preview overlay: the live vector map is coarser than the print
    // (no detail boost / supersample), so once the customer stops interacting we
    // paint the EXACT export render on top. It lives in the canvas container,
    // BELOW the draggable marker, and is pointer-transparent so drags pass
    // through. Hidden the instant they grab the map, restored when they settle.
    const overlay = document.createElement("img");
    overlay.style.cssText = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;pointer-events:none;opacity:0;transition:opacity .18s ease;z-index:1;";
    map.getCanvasContainer().appendChild(overlay);
    overlayRef.current = overlay;

    // Loupe: a circular magnifier that follows the cursor and enlarges the LIVE
    // map so the customer can inspect the fine detail exactly as it will print.
    // It reads the map's own WebGL canvas (preserveDrawingBuffer is on), so it
    // always works the instant Magnify is on — no dependency on the print overlay.
    const LOUPE_D = 170, LOUPE_ZOOM = 2.6;
    const loupe = document.createElement("canvas");
    loupe.width = LOUPE_D; loupe.height = LOUPE_D;
    loupe.style.cssText = "position:absolute;left:0;top:0;width:" + LOUPE_D + "px;height:" + LOUPE_D + "px;border-radius:50%;pointer-events:none;opacity:0;transition:opacity .1s ease;z-index:5;box-shadow:0 6px 22px rgba(0,0,0,.28),0 0 0 2px rgba(255,255,255,.92),0 0 0 4px rgba(0,0,0,.14);transform:translate(-50%,-50%);";
    const lctx = loupe.getContext("2d");
    const cont = map.getCanvasContainer();
    cont.appendChild(loupe);
    const showLoupe = (ev) => {
      if (!p.current.loupe) { loupe.style.opacity = "0"; return; }
      const cv = map.getCanvas();
      const r = cv.getBoundingClientRect();
      const x = ev.clientX - r.left, y = ev.clientY - r.top;
      if (x < 0 || y < 0 || x > r.width || y > r.height) { loupe.style.opacity = "0"; return; }
      // Prefer the PRINT render (rendered from deeper tiles) so magnifying shows
      // the real printed detail; fall back to the live canvas so the button always
      // does something, even before the print preview has finished.
      const ov = overlayRef.current;
      const usePrint = ov && ov.naturalWidth > 1 && ov.style.opacity === "1";
      const src = usePrint ? ov : cv;
      const sw = usePrint ? ov.naturalWidth : cv.width, sh = usePrint ? ov.naturalHeight : cv.height;
      const sx = sw / r.width, sy = sh / r.height;
      const srcW = (LOUPE_D / LOUPE_ZOOM) * sx, srcH = (LOUPE_D / LOUPE_ZOOM) * sy;
      lctx.clearRect(0, 0, LOUPE_D, LOUPE_D);
      try { lctx.drawImage(src, x * sx - srcW / 2, y * sy - srcH / 2, srcW, srcH, 0, 0, LOUPE_D, LOUPE_D); } catch (e) { return; }
      loupe.style.left = x + "px"; loupe.style.top = y + "px";
      loupe.style.opacity = "1";
    };
    const hideLoupe = () => { loupe.style.opacity = "0"; };
    cont.addEventListener("mousemove", showLoupe);
    cont.addEventListener("mouseleave", hideLoupe);
    cont.addEventListener("mousedown", hideLoupe);
    loupeRef.current = { hide: hideLoupe };

    let ovToken = 0;
    const renderOverlay = async () => {
      const my = ++ovToken;
      const holder = holderRef.current; if (!holder) return;
      const cssW = holder.clientWidth, cssH = holder.clientHeight;
      if (!cssW || !cssH) return;
      // WYSIWYG at PRINT scale: render the preview several zoom levels deeper (as
      // the real paper does) so the customer sees the finer detail that will
      // actually print, then let the browser scale the image down into the map
      // area. The loupe reads this image, so magnifying reveals true print detail.
      const boost = (typeof window !== "undefined" && window.SOUVENIR_PREVIEW_BOOST) || 3;
      const zoff = Math.log2(boost);
      const c = map.getCenter(), z = map.getZoom(); const pr = p.current;
      let mc = null;
      try { mc = await window.SouvenirGL.renderMapImage({ theme: pr.theme, lng: c.lng, lat: c.lat, zoom: z + zoff, zoomOffset: zoff, W: Math.round(cssW * boost), H: Math.round(cssH * boost), labels: pr.showLabels, detail: pr.detail, readyMs: 12000 }); } catch (e) { return; }
      if (!mc || my !== ovToken) return;
      try { overlay.src = mc.toDataURL("image/png"); } catch (e) { return; }
      overlay.style.opacity = "1";
    };
    const hideOverlay = () => { ovToken++; overlay.style.opacity = "0"; if (loupeRef.current) loupeRef.current.hide(); };
    let ovT = null;
    const scheduleOverlay = () => { clearTimeout(ovT); ovT = setTimeout(renderOverlay, 260); };
    overlayRef.current._render = scheduleOverlay;
    ["movestart", "zoomstart", "dragstart", "rotatestart", "pitchstart"].forEach((ev) => map.on(ev, hideOverlay));
    map.on("idle", scheduleOverlay);

    map.on("load", () => { setMarker(); scheduleOverlay(); });
    map.on("zoom", () => {
      const z = map.getZoom(); const cb = p.current.onZoom;
      if (cb && z !== lastZoom.current) { lastZoom.current = z; cb(z); }
    });
    map.on("move", (ev) => { if (markerRef.current && !markerRef.current._dragging) markerRef.current.setLngLat(map.getCenter()); });
    const ro = new ResizeObserver(() => { map.resize(); hideOverlay(); scheduleOverlay(); }); roRef.current = ro; ro.observe(holderRef.current);

    if (apiRef) apiRef.current = {
      recenter: () => { const pr = p.current; map.jumpTo({ center: [pr.location.lng, pr.location.lat], zoom: pr.location.zoom || 14 }); if (markerRef.current) markerRef.current.setLngLat([pr.location.lng, pr.location.lat]); },
      zoomIn: () => map.easeTo({ zoom: clamp(map.getZoom() + 0.6), duration: 220 }),
      zoomOut: () => map.easeTo({ zoom: clamp(map.getZoom() - 0.6), duration: 220 }),
      capture: async () => { await window.SouvenirGL.mapReady(map, 4000); return capturePoster(glTm(), p.current, 760); },
      getView: () => {
        const c = map.getCenter(); const mk = markerRef.current; const pin = mk ? mk.getLngLat() : c;
        return { lng: c.lng, lat: c.lat, zoom: map.getZoom(), pin: { lng: pin.lng, lat: pin.lat }, cssW: holderRef.current.clientWidth || 0, cssH: holderRef.current.clientHeight || 0 };
      },
      capturePrint: async (longEdgePx, fullRes) => { await window.SouvenirGL.mapReady(map, 6000); return capturePoster(glTm(), p.current, longEdgePx, fullRes); },
    };
    return () => { ro.disconnect(); if (markerRef.current) markerRef.current.remove(); map.remove(); mapRef.current = null; };
  }, []);

  useE(() => { const map = mapRef.current; if (map) { if (overlayRef.current) overlayRef.current.style.opacity = "0"; map.jumpTo({ center: [location.lng, location.lat], zoom: location.zoom || 14 }); if (markerRef.current) markerRef.current.setLngLat([location.lng, location.lat]); } }, [location.lng, location.lat, location.zoom]);
  useE(() => { const map = mapRef.current; if (map) { if (overlayRef.current) overlayRef.current.style.opacity = "0"; map.setStyle(window.buildGLStyle(theme, { labels: p.current.showLabels })); map.once("styledata", () => { setMarker(); if (overlayRef.current && overlayRef.current._render) overlayRef.current._render(); }); } }, [theme, props.showLabels, props.detail]);
  useE(() => { if (mapRef.current) setMarker(); }, [markerStyle, props.markerScale, props.markerColor]);
  useE(() => { if (loupeRef.current && !props.loupe) loupeRef.current.hide(); if (holderRef.current) holderRef.current.style.cursor = props.loupe ? "zoom-in" : ""; }, [props.loupe]);
  // Shaped borders change the holder from full-bleed to a centred square, so the map
  // must be re-measured AFTER layout settles — one rAF lands too early (aspect-ratio +
  // container queries resolve a frame later), which left the canvas at its old size and
  // cropped the map instead of reframing it.
  useE(() => {
    const map = mapRef.current; if (!map) return;
    const kick = () => { map.resize(); if (overlayRef.current && overlayRef.current._render) overlayRef.current._render(); };
    const r1 = requestAnimationFrame(() => { map.resize(); requestAnimationFrame(kick); });
    const t1 = setTimeout(kick, 140), t2 = setTimeout(kick, 360);
    return () => { cancelAnimationFrame(r1); clearTimeout(t1); clearTimeout(t2); };
  }, [paper.key, paper.aspect, paper.landscape, labels.layout, props.frameMat]);

  const e = React.createElement;
  const mapEl = e("div", { key: "map", style: { flex: "1 1 auto", minHeight: 0, position: "relative" } },
    e("div", { ref: holderRef, className: "ofm-map", style: { position: "absolute", inset: 0 } }));
  return renderShell(Object.assign({}, props, { boxRef }), mapEl);
}

// ---- Canvas poster (aerial / land-use products) -----------------------------
function CanvasPoster(props) {
  const { location, theme, markerStyle, detail, paper, labels, apiRef } = props;
  const ZMIN = (typeof window !== "undefined" && window.SOUVENIR_MINZOOM) || 3;
  const ZMAX = (typeof window !== "undefined" && window.SOUVENIR_MAXZOOM) || (theme && theme.satellite ? 17 : 15);
  const canvasRef = useR(null);
  const boxRef = useR(null);
  const roRef = useR(null);
  const canvasCb = useR(null);
  if (!canvasCb.current) canvasCb.current = (node) => {
    canvasRef.current = node;
    if (roRef.current) roRef.current.disconnect();
    const tm = tmRef.current;
    if (node) {
      if (roRef.current) roRef.current.observe(node);
      if (tm) requestAnimationFrame(() => { if (canvasRef.current === node) { tm.attach(node); tm.resize(); } });
    }
  };
  const tmRef = useR(null);
  const drag = useR(null);
  const wheelT = useR(0);
  const lastZoom = useR(location.zoom || 13);
  const p = useR(props); p.current = props;

  useE(() => {
    const tm = new window.TileMap({
      canvas: canvasRef.current, theme, detail, markerStyle, markerScale: props.markerScale, markerColor: props.markerColor,
      lng: location.lng, lat: location.lat, zoom: location.zoom || 13,
      onView: (view) => { const cb = p.current.onZoom; if (cb && view.zoom !== lastZoom.current) { lastZoom.current = view.zoom; cb(view.zoom); } },
    });
    tmRef.current = tm;
    const ro = new ResizeObserver(() => tm.resize()); roRef.current = ro;
    if (canvasRef.current) ro.observe(canvasRef.current);

    if (apiRef) apiRef.current = {
      recenter: () => { const pr = p.current; tm.setPin(pr.location.lng, pr.location.lat); tm.setView({ lng: pr.location.lng, lat: pr.location.lat, zoom: pr.location.zoom || 13 }); },
      zoomIn: () => { tm.setDragging(true); tm.setView({ zoom: window.TileMapHelpers.clamp(tm.view.zoom + 0.6, ZMIN, ZMAX) }); clearTimeout(wheelT.current); wheelT.current = setTimeout(() => tm.setDragging(false), 220); },
      zoomOut: () => { tm.setDragging(true); tm.setView({ zoom: window.TileMapHelpers.clamp(tm.view.zoom - 0.6, ZMIN, ZMAX) }); clearTimeout(wheelT.current); wheelT.current = setTimeout(() => tm.setDragging(false), 220); },
      capture: async () => {
        boxRef.current && (tm.boxW = boxRef.current.clientWidth, tm.boxH = boxRef.current.clientHeight);
        await Promise.race([tm.idle(), new Promise((r) => setTimeout(r, 2500))]);
        capturePoster(tm, p.current, 760);
        await Promise.race([tm.idle(), new Promise((r) => setTimeout(r, 3500))]);
        await new Promise((r) => setTimeout(r, 30));
        return capturePoster(tm, p.current, 760);
      },
      getView: () => ({ lng: tm.view.lng, lat: tm.view.lat, zoom: tm.view.zoom, pin: tm.pin ? { lng: tm.pin.lng, lat: tm.pin.lat } : null, cssW: (tm.canvas && tm.canvas.clientWidth) || 0, cssH: (tm.canvas && tm.canvas.clientHeight) || 0 }),
      capturePrint: async (longEdgePx, fullRes) => {
        boxRef.current && (tm.boxW = boxRef.current.clientWidth, tm.boxH = boxRef.current.clientHeight);
        await Promise.race([tm.idle(), new Promise((r) => setTimeout(r, 4000))]);
        capturePoster(tm, p.current, 760);
        await Promise.race([tm.idle(), new Promise((r) => setTimeout(r, 5000))]);
        capturePoster(tm, p.current, longEdgePx, fullRes);
        await Promise.race([tm.idle(), new Promise((r) => setTimeout(r, 20000))]);
        await new Promise((r) => setTimeout(r, 30));
        return capturePoster(tm, p.current, longEdgePx, fullRes);
      },
    };
    return () => { ro.disconnect(); if (tm._raf) cancelAnimationFrame(tm._raf); tmRef.current = null; };
  }, []);

  useE(() => { const tm = tmRef.current; if (!tm) return; tm.setPin(location.lng, location.lat); tm.setView({ lng: location.lng, lat: location.lat, zoom: location.zoom || 13 }); }, [location.lng, location.lat, location.zoom]);
  useE(() => { const tm = tmRef.current; if (tm) tm.setTheme(theme, detail); }, [theme, detail]);
  useE(() => { const tm = tmRef.current; if (tm) tm.setMarker(markerStyle); }, [markerStyle]);
  useE(() => { const tm = tmRef.current; if (tm && tm.setMarkerScale) tm.setMarkerScale(props.markerScale); }, [props.markerScale]);
  useE(() => { const tm = tmRef.current; if (tm && tm.setMarkerColor) tm.setMarkerColor(props.markerColor); }, [props.markerColor]);
  useE(() => {
    const tm = tmRef.current; if (!tm) return;
    const kick = () => { if (canvasRef.current) { tm.attach(canvasRef.current); tm.resize(); } };
    const r1 = requestAnimationFrame(() => { kick(); requestAnimationFrame(kick); });
    const t1 = setTimeout(kick, 140), t2 = setTimeout(kick, 360);
    return () => { cancelAnimationFrame(r1); clearTimeout(t1); clearTimeout(t2); };
  }, [paper.key, paper.aspect, paper.landscape, labels.layout, props.frameMat]);

  const onDown = (ev) => {
    const tm = tmRef.current; if (!tm) return;
    const r = canvasRef.current.getBoundingClientRect();
    if (tm.hitPin(ev.clientX - r.left, ev.clientY - r.top)) { drag.current = { pin: true, rect: r }; canvasRef.current.style.cursor = "grabbing"; }
    else { tm.setDragging(true); canvasRef.current.style.cursor = "grabbing"; drag.current = { x: ev.clientX, y: ev.clientY, lng: tm.view.lng, lat: tm.view.lat, zoom: tm.view.zoom }; }
    try { canvasRef.current.setPointerCapture(ev.pointerId); } catch (err) {}
  };
  const onMove = (ev) => {
    const d = drag.current, tm = tmRef.current; if (!tm) return;
    if (d && d.pin) { const r = d.rect; const ll = tm.screenToLngLat(ev.clientX - r.left, ev.clientY - r.top); if (ll) tm.setPin(ll.lng, ll.lat); return; }
    if (!d) { const r = canvasRef.current.getBoundingClientRect(); canvasRef.current.style.cursor = tm.hitPin(ev.clientX - r.left, ev.clientY - r.top) ? "move" : "grab"; return; }
    const H = window.TileMapHelpers; const ws = 256 * Math.pow(2, d.zoom);
    const wx = H.lon2x(d.lng, ws) - (ev.clientX - d.x); const wy = H.lat2y(d.lat, ws) - (ev.clientY - d.y);
    tm.setView({ lng: H.x2lon(wx, ws), lat: H.y2lat(H.clamp(wy, 1, ws - 1), ws) });
  };
  const onUp = (ev) => {
    const c = canvasRef.current;
    if (c && c.hasPointerCapture && c.hasPointerCapture(ev.pointerId)) c.releasePointerCapture(ev.pointerId);
    const tm = tmRef.current; if (tm) tm.setDragging(false); if (c) c.style.cursor = "grab"; drag.current = null;
  };
  const onWheel = (ev) => {
    ev.preventDefault(); const tm = tmRef.current; if (!tm) return;
    tm.setDragging(true);
    tm.setView({ zoom: window.TileMapHelpers.clamp(tm.view.zoom + (ev.deltaY < 0 ? 0.5 : -0.5), ZMIN, ZMAX) });
    clearTimeout(wheelT.current); wheelT.current = setTimeout(() => tm.setDragging(false), 200);
  };

  const e = React.createElement;
  const mapEl = e("div", { key: "map", style: { flex: "1 1 auto", minHeight: 0, position: "relative" } },
    e("canvas", { ref: canvasCb.current, onPointerDown: onDown, onPointerMove: onMove, onPointerUp: onUp, onPointerCancel: onUp, onWheel, style: { width: "100%", height: "100%", display: "block", cursor: drag.current ? "grabbing" : "grab", touchAction: "none" } }));
  return renderShell(Object.assign({}, props, { boxRef }), mapEl);
}

// render the poster CONTENT (map + caption, no border) into a W×H canvas.
async function renderPosterContent(tm, pr, W, H) {
  const canvas = tm.canvas;
  const liveCssW = (canvas && (canvas.clientWidth || canvas.width / (tm.dpr || 1))) || W;
  const liveCssH = (canvas && (canvas.clientHeight || canvas.height / (tm.dpr || 1))) || Math.round(H * 0.8);
  const boxW = tm.boxW || liveCssW, boxH = tm.boxH || liveCssH;
  let mapH = pr.labels.show ? Math.round(W * (boxH / boxW)) : H;
  mapH = Math.max(1, Math.min(mapH, H));
  const lay = pr.labels.layout;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d");
  ctx.fillStyle = sheetBg(pr.theme, pr.frameMat); ctx.fillRect(0, 0, W, H);
  const vBlock = pr.theme.vectorBlocks && window.renderLanduseBlocks;
  const mkTheme = pr.markerColor ? Object.assign({}, pr.theme, { marker: pr.markerColor }) : pr.theme;
  const shape = isShaped(pr.frameMat) ? pr.frameMat : null;
  const isoTall = shape && pr.paper && pr.paper.aspect > 1.02;
  const shapeFixed = shape && pr.paper && pr.paper.aspect >= 0.995;

  const paintMap = async (mx, my, mw, mh) => {
    // MapLibre GL product: render the map area off-screen at print pixels, then
    // draw the marker on top so it stays crisp.
    if (tm.isGL) {
      const zoomP = tm.view.zoom + Math.log2(Math.max(0.2, mw / liveCssW));
      let mc = null;
      try { mc = await window.SouvenirGL.renderMapImage({ theme: pr.theme, lng: tm.view.lng, lat: tm.view.lat, zoom: zoomP, zoomOffset: zoomP - tm.view.zoom, W: Math.round(mw), H: Math.round(mh), labels: pr.showLabels, detail: pr.detail }); } catch (e) { /* paper */ }
      if (mc) ctx.drawImage(mc, mx, my, mw, mh);
      if (tm.markerStyle && tm.markerStyle !== "none" && window.drawMarker) {
        const pin = tm.pin || { lng: tm.view.lng, lat: tm.view.lat };
        const pp = window.SouvenirGL.project(pin.lng, pin.lat, { lng: tm.view.lng, lat: tm.view.lat }, zoomP, mw, mh);
        // Mirror the on-screen marker exactly: makeMarkerEl draws at 0.62×scale
        // in live CSS px, so the print uses the same ratio against the map width.
        // This keeps the printed pin WYSIWYG and makes the size slider apply.
        const f = (mw / liveCssW) * 0.62 * (pr.markerScale || 1);
        ctx.save(); ctx.translate(mx + pp.x, my + pp.y); ctx.scale(f, f); window.drawMarker(ctx, tm.markerStyle, mkTheme, 0, 0); ctx.restore();
      }
      return;
    }
    if (vBlock) {
      const zoomP = tm.view.zoom + Math.log2(Math.max(0.2, mw / liveCssW));
      let mc = null;
      try { mc = await window.renderLanduseBlocks({ lng: tm.view.lng, lat: tm.view.lat, zoom: zoomP, W: mw, H: mh, dpr: 1, palette: pr.theme.palette, maptilerKey: (window.MAPTILER_KEY || ""), buildingTileset: (window.SOUVENIR_BUILDINGS || "buildings") }); } catch (e) { /* leave paper */ }
      if (mc) ctx.drawImage(mc, mx, my, mw, mh);
      if (tm.markerStyle && tm.markerStyle !== "none" && window.drawMarker) {
        const pin = tm.pin || { lng: tm.view.lng, lat: tm.view.lat };
        const pp = tm._proj(pin.lng, pin.lat, mw, mh, { lng: tm.view.lng, lat: tm.view.lat, zoom: zoomP }, 1);
        // Mirror the on-screen marker exactly: makeMarkerEl draws at 0.62×scale
        // in live CSS px, so the print uses the same ratio against the map width.
        // This keeps the printed pin WYSIWYG and makes the size slider apply.
        const f = (mw / liveCssW) * 0.62 * (pr.markerScale || 1);
        ctx.save(); ctx.translate(mx + pp.x, my + pp.y); ctx.scale(f, f); window.drawMarker(ctx, tm.markerStyle, mkTheme, 0, 0); ctx.restore();
      }
    } else {
      const tmp = document.createElement("canvas"); tmp.width = mw; tmp.height = mh;
      tm.render({ ctx: tmp.getContext("2d"), w: mw, h: mh, scale: mw / liveCssW, view: tm.view });
      ctx.drawImage(tmp, mx, my);
    }
  };

  // Shaped borders: clip the map to a centred square of the map box, then trace the
  // same path as a hairline. Identical proportions to the on-screen clip (SHAPE_FILL).
  const paintMapShaped = async (mx, my, mw, mh, anchorBottom) => {
    if (!shape) return paintMap(mx, my, mw, mh);
    // When the preview pins the map box to the shape's own size (shapeFixed: every
    // portrait and square shaped poster), the box height IS the shape side —
    // multiplying by SHAPE_FILL again shrinks it twice. Harmless on portrait, but on
    // a square page the box is only ~76% of the width, so the shape came out small.
    const side = shapeFixed ? Math.round(Math.min(mw, mh)) : Math.round(Math.min(mw, mh) * SHAPE_FILL);
    const sx = Math.round(mx + (mw - side) / 2), sy = Math.round(anchorBottom ? my + (mh - side) : my + (mh - side) / 2);
    ctx.save();
    shapePath(ctx, shape, sx, sy, side, side);
    ctx.clip();
    await paintMap(sx, sy, side, side);
    ctx.restore();
    ctx.save();
    shapePath(ctx, shape, sx, sy, side, side);
    // Same ink as the caption (“Paris”), so the outline reads as part of the type.
    ctx.strokeStyle = pr.theme.labelColor || pr.theme.text || "rgba(48,38,24,.5)";
    ctx.globalAlpha = 0.85;
    ctx.lineWidth = Math.max(1, Math.round(side * 0.002));
    ctx.stroke();
    ctx.restore();
  };

  if (lay === "1b") {
    const padX = Math.round(W * 0.075), padY = Math.round(H * 0.05);
    const topRowH = Math.round(H * 0.03), botH = Math.round(H * 0.12);
    const mapX = padX, mapTopY = padY + topRowH + Math.round(H * 0.022);
    const mapW = W - padX * 2, mapBoxH = Math.max(1, H - mapTopY - botH - Math.round(padY * 0.4));
    await paintMapShaped(mapX, mapTopY, mapW, mapBoxH);
    if (window.drawPoster1b) window.drawPoster1b(ctx, pr.labels, pr.theme, pr.titleFont, W, padX, padY, topRowH, mapTopY + mapBoxH, botH);
  } else if (lay === "1a") {
    await paintMapShaped(0, 0, W, mapH);
    if (pr.labels.show && H - mapH > 0 && window.drawPoster1a) window.drawPoster1a(ctx, pr.labels, pr.theme, pr.titleFont, W, mapH, H - mapH);
  } else {
    // Geometry is READ FROM THE LIVE DOM rather than re-derived from hand-tuned
    // percentages: the preview is on screen at export time, so its caption box
    // height / padding / poster width are measurable facts. Scale them by the single
    // print:preview width ratio and the two layouts cannot disagree.
    const posterEl = tm.canvas && tm.canvas.closest && tm.canvas.closest(".live-poster");
    const capEl = posterEl && pr.labels.show ? posterEl.lastElementChild : null;
    const cssW = posterEl ? posterEl.getBoundingClientRect().width : 0;
    const kW = cssW > 0 ? W / cssW : ((tm.boxH && mapH) ? mapH / tm.boxH : 1);
    let capBoxH = 0, padTop = 0, rowCentres = null, ruleGeom = null;
    if (capEl) {
      const cs = window.getComputedStyle(capEl);
      capBoxH = capEl.offsetHeight * kW;
      padTop = (parseFloat(cs.paddingTop) || 0) * kW;
      // Each caption row's own centre, measured live and scaled — the row rhythm is
      // copied outright instead of re-simulated from font sizes, gaps and
      // line-heights (which is what kept the printed rows crowding together).
      const capTopCss = capEl.getBoundingClientRect().top;
      rowCentres = [...capEl.children].map((el) => {
        const r = el.getBoundingClientRect();
        return ((r.top + r.bottom) / 2 - capTopCss) * kW;
      });
      // The divider rule's real geometry, so the printed lines + dot match the
      // preview's span instead of the old hardcoded 16%-of-width half-lines.
      const ruleEl = [...capEl.children].find((el) => el.childElementCount === 3);
      if (ruleEl) {
        const rr = ruleEl.getBoundingClientRect();
        const dr = ruleEl.children[1].getBoundingClientRect(), lr = ruleEl.children[0].getBoundingClientRect();
        ruleGeom = { w: rr.width * kW, line: lr.width * kW, dot: Math.max(4, H * 0.009), lineH: Math.max(1, H * 0.001) };
      }
    }
    const capNat = (pr.labels.show && window.measureCaption) ? window.measureCaption(pr.labels, W, H, kW) : 0;
    if (capEl && capBoxH > 0) {
      const topOff = shapeFixed ? Math.max(0, Math.round((H - (mapH + capBoxH)) / 2)) : 0;
      await paintMapShaped(0, topOff, W, mapH, false);
      // Absolute row centres = caption box top + each measured offset.
      const capTop = topOff + mapH;
      if (window.drawCaption) window.drawCaption(ctx, pr.labels, pr.theme, pr.titleFont, W, capTop, capBoxH, false, false, H, kW, rowCentres.map((o) => capTop + o), ruleGeom);
    } else {
      await paintMapShaped(0, 0, W, mapH, false);
      if (pr.labels.show && H - mapH > 0 && window.drawCaption) window.drawCaption(ctx, pr.labels, pr.theme, pr.titleFont, W, mapH, H - mapH, !!shape, !!isoTall, H, kW);
    }
  }
  return cv;
}

function capturePoster(tm, pr, targetW, fullRes) { return _capturePosterAsync(tm, pr, targetW, fullRes); }
async function _capturePosterAsync(tm, pr, targetW, fullRes) {
  const aspect = pr.paper.aspect;
  const SAFE = fullRes ? 12000 : 3800;
  const longPerShort = aspect >= 1 ? aspect : 1 / aspect;
  let baseW = targetW <= 900 ? targetW : Math.min(targetW, fullRes ? 9000 : 4200);
  if (baseW * longPerShort > SAFE) baseW = Math.floor(SAFE / longPerShort);
  const totalH = Math.round(baseW * aspect);
  const border = pr.frameMat || "none";
  const mn = Math.min(baseW, totalH);
  const inset = border === "mat" ? Math.round(mn * 0.065) : border === "line" ? Math.round(mn * 0.028) : 0;
  const content = await renderPosterContent(tm, pr, baseW - inset * 2, totalH - inset * 2);
  const cv = document.createElement("canvas");
  cv.width = baseW; cv.height = totalH;
  const ctx = cv.getContext("2d");
  ctx.fillStyle = (border === "none" || isShaped(border)) ? sheetBg(pr.theme, border) : "#fbf8f1";
  ctx.fillRect(0, 0, baseW, totalH);
  ctx.drawImage(content, inset, inset);
  if (border === "line") {
    ctx.strokeStyle = "rgba(48,38,24,.55)";
    ctx.lineWidth = Math.max(1, Math.round(baseW * 0.0016));
    ctx.strokeRect(inset, inset, baseW - inset * 2, totalH - inset * 2);
  }
  const big = fullRes || baseW * totalH > 20e6;
  try { return cv.toDataURL(big ? "image/jpeg" : "image/png", big ? 0.92 : undefined); } catch (e) { return ""; }
}

window.LiveMapPoster = LiveMapPoster;
